import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  StatusBadge,
} from '@app/ui';
import { PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import { formatCurrency, formatDateShort } from '@app/validators';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

const paymentStatusEnum = z.enum(['PENDING', 'RECEIVED', 'OVERDUE', 'CANCELLED']);
type PaymentStatus = z.infer<typeof paymentStatusEnum>;

export const Route = createFileRoute('/_authenticated/payments')({
  validateSearch: z.object({
    status: paymentStatusEnum.optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await Promise.all([
      context.queryClient.ensureQueryData(
        serverApi.payment.list.queryOptions({
          input: {
            status: deps.status,
            page: deps.page,
            limit: 20,
          },
        }),
      ),
      context.queryClient.ensureQueryData(serverApi.payment.summary.queryOptions()),
    ]);
  },
  pendingComponent: () => (
    <div className="animate-pulse">
      <div className="h-8 bg-muted rounded w-48 mb-6" />
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-muted rounded" />
        ))}
      </div>
    </div>
  ),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: Payments,
});

const STATUS_FILTERS = ['All', 'Pending', 'Received', 'Overdue'] as const;

function Payments() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  const { data: paymentData } = useSuspenseQuery(
    api.payment.list.queryOptions({
      input: {
        status: search.status,
        page: search.page,
        limit: 20,
      },
    }),
  );
  const { data: summary } = useSuspenseQuery(api.payment.summary.queryOptions());

  const { payments, page, totalPages } = paymentData;

  const markReceivedMutation = useMutation({
    ...api.payment.markReceived.mutationOptions(),
    onSuccess: () => {
      // Invalidate all payment.list queries regardless of filters.
      // oRPC queryKey is [path[], options]; queryKey[0] is the path array.
      queryClient.invalidateQueries({
        predicate: (query) => {
          const path = query.queryKey?.[0];
          return Array.isArray(path) && path[0] === 'payment' && path[1] === 'list';
        },
      });
      queryClient.invalidateQueries({ queryKey: api.payment.summary.queryKey() });
    },
  });

  const handleMarkReceived = (paymentId: string) => {
    markReceivedMutation.mutate({ id: paymentId });
  };

  return (
    <PageTransition>
      <h1 className="text-2xl sm:text-3xl font-bold mb-2">Payments</h1>
      <p className="text-muted-foreground mb-6">
        Track expected vs received payments across all your deals
      </p>

      <StaggerContainer className="grid sm:grid-cols-3 gap-4 mb-6">
        <StaggerItem>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total Received</p>
              <p className="text-2xl font-semibold text-green-600 mt-1">
                {formatCurrency(summary.received)}
              </p>
            </CardContent>
          </Card>
        </StaggerItem>
        <StaggerItem>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-2xl font-semibold text-yellow-600 mt-1">
                {formatCurrency(summary.pending)}
              </p>
            </CardContent>
          </Card>
        </StaggerItem>
        <StaggerItem>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Overdue</p>
              <p className="text-2xl font-semibold text-red-600 mt-1">
                {formatCurrency(summary.overdue)}
              </p>
            </CardContent>
          </Card>
        </StaggerItem>
      </StaggerContainer>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_FILTERS.map((status) => {
          const isActive =
            (status === 'All' && !search.status) || status.toUpperCase() === search.status;
          return (
            <Button
              key={status}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() =>
                navigate({
                  search: {
                    status: status === 'All' ? undefined : (status.toUpperCase() as PaymentStatus),
                    page: 1,
                  },
                })
              }
            >
              {status}
            </Button>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Payments</CardTitle>
          <CardDescription>Sorted by due date. Click deal name to view details.</CardDescription>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">
              Payment history will appear here as you log deals.
            </p>
          ) : (
            <div className="space-y-2">
              {payments.map((p) => (
                <div
                  key={p.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b last:border-b-0"
                >
                  <div>
                    <Link
                      to="/deals/$dealId"
                      params={{ dealId: p.dealId }}
                      className="text-sm font-medium hover:underline"
                    >
                      {p.dealTitle}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {p.brandName} &middot;{' '}
                      {p.dueDate ? `Due ${formatDateShort(p.dueDate)}` : 'No due date'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-medium">{formatCurrency(p.amount)}</p>
                    <StatusBadge status={p.status} />
                    {(p.status === 'PENDING' || p.status === 'OVERDUE') && (
                      <Button variant="outline" size="sm" onClick={() => handleMarkReceived(p.id)}>
                        Mark Received
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => navigate({ search: { ...search, page: page - 1 } })}
              >
                Previous
              </Button>
              <span className="flex items-center text-sm text-muted-foreground px-3">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => navigate({ search: { ...search, page: page + 1 } })}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </PageTransition>
  );
}
